import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../utils/api';
import { api_url } from '../config';

const initialState = {
  prescriptions: [],
  prescriptionDetail: {},
  prescriptionComments:[],
  medicationNames: [],
  loading: false,
  error: null,
  // New state for sorting and filtering
  sort: null,
  filters: {}
};

export const fetchPrescriptions = createAsyncThunk(
  'prescriptions/fetchPrescriptions',
  async ({
    userId,
    branchId,
    sort = null,
    filters = {}
  }, { getState }) => {
    const { auth: { token } } = getState();

    // Construct query parameters
    const params = {};

    // Add sorting
    if (sort) {
      params.sort = sort;
    }

    // Add filters
    Object.keys(filters).forEach(key => {
      params[`filter[${key}]`] = filters[key];
    });

    console.log('Fetching prescriptions with params:', params);

    const response = await api.get(
      `${api_url}/api/v1/retailer/user/${userId}/branch/${branchId}/prescription`,
      {
        params,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      }
    );

    console.log('API Response:', response.data);

    // Return just the data array
    return response.data.data || [];
  }
);

export const addPrescription = createAsyncThunk(
  'prescriptions/addPrescription',
  async (prescriptionData, { getState, rejectWithValue }) => {
    try {
      const { auth: { token, id, branch_id } } = getState();
      
      // Validate required fields
      if (!prescriptionData.medications || !Array.isArray(prescriptionData.medications) || prescriptionData.medications.length === 0) {
        throw new Error('At least one medication is required');
      }

      if (!prescriptionData.patient_id) {
        throw new Error('Patient ID is required');
      }

      // Format the prescription data according to API requirements
      const prescriptionWithDefaults = {
        order_source: prescriptionData.order_source || 'provider',
        type: prescriptionData.order_source === 'patient' ? null : (prescriptionData.type || 'WRITTEN'),
        date_issued: new Date(prescriptionData.date_issued) > new Date() ? 
          new Date().toISOString().split('T')[0] : 
          prescriptionData.date_issued || new Date().toISOString().split('T')[0],
        patient_id: Number(prescriptionData.patient_id),
        comment: prescriptionData.comment || "",
        ...(prescriptionData.order_source === 'provider' ? {
          provider: {
            first_name: prescriptionData.provider?.first_name || "",
            last_name: prescriptionData.provider?.last_name || "",
            title: prescriptionData.provider?.title || "",
            facility: prescriptionData.provider?.facility || "",
            phone_number: prescriptionData.provider?.phone_number || "",
            provider_id: prescriptionData.provider?.provider_id || ""
          }
        } : {}),
        medications: prescriptionData.medications.map(med => {
          // Ensure medication_name_id is present and is a number
          if (!med.medication_name_id) {
            throw new Error('medication_name_id is required for each medication');
          }
          
          // Validate required medication fields
          if (!med.strength) throw new Error('Strength is required for each medication');
          if (!med.quantity) throw new Error('Quantity is required for each medication');
          if (!med.unit) throw new Error('Unit is required for each medication');
          if (!med.dosage_form) throw new Error('Dosage form is required for each medication');
          if (!med.route) throw new Error('Route is required for each medication');
          if (!med.frequency) throw new Error('Frequency is required for each medication');
          
          return {
            medication_name_id: Number(med.medication_name_id),
            strength: String(med.strength),
            quantity: Number(med.quantity),
            refills: Number(med.refills || 0),
            instructions: med.instructions || "Take as prescribed",
            unit: med.unit,
            dosage_form: med.dosage_form,
            route: med.route,
            frequency: med.frequency
          };
        })
      };

      // Log the request for debugging
      console.log("Sending prescription data to API:", JSON.stringify(prescriptionWithDefaults, null, 2));

      const response = await api.post(
        `${api_url}/api/v1/retailer/user/${id}/branch/${branch_id}/prescription`,
        prescriptionWithDefaults,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        }
      );

      // Check if the response contains data
      if (!response.data) {
        throw new Error('No data received from server');
      }

      // Check if the response contains an error
      if (response.data.error) {
        throw new Error(response.data.error);
      }

      // If we have medications in the response, update the prescription with the first medication's ID
      if (response.data.data?.medications?.length > 0) {
        const firstMedicationId = response.data.data.medications[0].id;
        
        // Update the prescription with the medication ID
        await api.put(
          `${api_url}/api/v1/retailer/user/${id}/branch/${branch_id}/prescription/${response.data.data.id}`,
          { medication_id: firstMedicationId },
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            }
          }
        );
      }

      // Return the successful response data
      return response.data;
    } catch (error) {
      console.error("API Error:", error);
      
      // Handle authentication errors specifically
      if (error.response?.status === 401) {
        // Don't reject with value, let the auth middleware handle it
        throw error;
      }
      
      // For other errors, return a detailed error message
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to add prescription',
        details: error.response?.data?.errors || error.response?.data || 'Validation error'
      });
    }
  }
);

export const addComment = createAsyncThunk(
  'prescriptions/addComment',
  async ({ comment, prescription_id }, thunkAPI) => {
    try {
      const { auth: { token, id, branch_id } } = thunkAPI.getState();

      const response = await api.post(
        `${api_url}/api/v1/retailer/user/${id}/branch/${branch_id}/prescription/${prescription_id}/comments`,
        { content: comment }, // Wrap comment in an object
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        }
      );

      return response.data;
    } catch (error) {
      // Handle specific error scenarios
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        'Failed to add comments'
      );
    }
  }
);

export const updatePrescription = createAsyncThunk(
  'prescriptions/updatePrescription',
  async ({ prescriptionId, prescriptionData }, { getState, rejectWithValue }) => {
    try {
      const { auth: { token, id, branch_id } } = getState();
      
      // Get current prescription data from the state if available
      const { prescriptions } = getState();
      const currentPrescription = prescriptions.prescriptionDetail;
      
      // Ensure order_source is set, either from the provided data or from the current prescription
      const updatedPrescriptionData = {
        // If current prescription exists, use its order_source as fallback, otherwise default to 'provider'
        order_source: (currentPrescription?.order_source || 'provider'),
        ...prescriptionData,
      };

      // If order_source is 'patient', we can set provider_id to null if not specified
      if (updatedPrescriptionData.order_source === 'patient' && !updatedPrescriptionData.provider_id) {
        updatedPrescriptionData.provider_id = null;
      }

      const response = await api.put(
        `${api_url}/api/v1/retailer/user/${id}/branch/${branch_id}/prescription/${prescriptionId}`,
        updatedPrescriptionData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        'Failed to update prescription'
      );
    }
  }
);

export const updatePrescriptionStatus = createAsyncThunk(
  'prescriptions/updatePrescriptionStatus',
  async ({ prescriptionId, action }, { getState, rejectWithValue }) => {
    try {
      const { auth: { token, id, branch_id }, prescriptions } = getState();
      
      // Find the current prescription
      const currentPrescription = prescriptions.prescriptionDetail
      

      if (!currentPrescription) {
        return rejectWithValue('Prescription not found');
      }

      // Prepare the request data
      let prescriptionData;

      // If the action is explicitly to cancel, set status to cancelled
      if (action === 'cancel') {
        prescriptionData = { status: 'cancelled' };
      } else {
        // Normal progression logic
        let newStatus;
        switch (currentPrescription.status) {
          case 'pending':
            newStatus = 'processing';
            break;
          case 'processing':
            newStatus = 'awaiting_confirmation';
            break;
          case 'awaiting_confirmation':
            newStatus = 'pick_up';
            break;
          case 'pick_up':
            newStatus = 'fulfilled';
            break;
          default:
            return rejectWithValue('Invalid current status');
        }
        prescriptionData = { status: newStatus };
      }

      // Make the API call to update status
      const response = await api.put(
        `${api_url}/api/v1/retailer/user/${id}/branch/${branch_id}/prescription/${prescriptionId}/status`,
        prescriptionData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        'Failed to update prescription status'
      );
    }
  }
);

export const fetchPrescription = createAsyncThunk(
  'prescriptions/fetchPrescription',
  async (prescriptionId, { getState, rejectWithValue }) => {
    try {

      const { auth: { token, id, branch_id } } = getState();

      const response = await api.get(
        `${api_url}/api/v1/retailer/user/${id}/branch/${branch_id}/prescription/${prescriptionId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        'Failed to update prescription'
      );
    }
  }
);

export const fetchPrescriptionComments = createAsyncThunk(
  'prescriptions/fetchPrescriptionComments',
  async (prescriptionId, { getState, rejectWithValue }) => {
    try {

      const { auth: { token, id, branch_id } } = getState();

      const response = await api.get(
        `${api_url}/api/v1/retailer/user/${id}/branch/${branch_id}/prescription/${prescriptionId}/comments`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        'Failed to update prescription'
      );
    }
  }
);

// Fetch medication names
export const fetchMedicationNames = createAsyncThunk(
  'prescriptions/fetchMedicationNames',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { auth: { token } } = getState();
      console.log('Fetching medication names from API...');
      // Update the endpoint to match what the backend expects
      const endpoint = `${api_url}/api/v1/medication-names`;
      console.log(`URL: ${endpoint}`);
      
      const response = await api.get(endpoint, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      
      console.log('Medication names API response:', response);
      return response.data;
    } catch (error) {
      console.error('Error fetching medication names:', error);
      // Return mock data for development if API fails
      if (error.response?.status === 404) {
        console.log('Returning mock medication data since endpoint not found');
        return {
          data: [
            { id: 1, name: "Acetaminophen" },
            { id: 2, name: "Ibuprofen" },
            { id: 3, name: "Amoxicillin" }
          ]
        };
      }
      return rejectWithValue(error.message || 'Failed to fetch medication names');
    }
  }
);

// Create new medication name
export const createMedicationName = createAsyncThunk(
  'prescriptions/createMedicationName',
  async (medicationData, { getState, rejectWithValue }) => {
    try {
      const { auth: { token } } = getState();
      console.log('Creating new medication name:', medicationData);
      
      // Validate input data
      if (!medicationData || !medicationData.name || typeof medicationData.name !== 'string') {
        throw new Error('Invalid medication name data');
      }

      // Ensure we're sending the correct data format
      const requestData = {
        name: medicationData.name.trim()
      };

      const response = await api.post(
        `${api_url}/api/v1/medication-names`,
        requestData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        }
      );
      
      console.log('Create medication name response:', response);
      
      if (!response.data || !response.data.id) {
        throw new Error('Invalid response from server');
      }

      // Return the created medication name data
      return response.data;
    } catch (error) {
      console.error('Error creating medication name:', error);
      console.error('Error response:', error.response);
      return rejectWithValue(
        error.response?.data?.message || 
        error.message || 
        'Failed to create medication name'
      );
    }
  }
);

export const updateAllPrescriptions = createAsyncThunk(
  'prescriptions/updateAll',
  async ({ prescriptionIds, prescriptionData }, { getState, rejectWithValue }) => {
    try {
      const { auth: { token, id, branch_id } } = getState();
      
      // Since we're updating a single prescription, use the first ID
      const prescriptionId = prescriptionIds[0];
      
      const response = await api.put(
        `${api_url}/api/v1/retailer/user/${id}/branch/${branch_id}/prescription/${prescriptionId}/update-all`,
        prescriptionData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        'Failed to update prescriptions'
      );
    }
  }
);

export const fetchPatientPrescriptions = createAsyncThunk(
  'prescriptions/fetchPatientPrescriptions',
  async ({
    userId,
    branchId,
    patientId,
    sort = null,
    filters = {}
  }, { getState, rejectWithValue }) => {
    try {
      const { auth: { token } } = getState();

      if (!token) {
        throw new Error('Authentication token is missing');
      }

      // Log the request details for debugging
      console.log('Fetching patient prescriptions with:', {
        userId,
        branchId,
        patientId,
        token: token ? 'Token exists' : 'No token',
        params: { sort, filters }
      });

      // Construct query parameters
      const params = {};

      // Add sorting
      if (sort) {
        params.sort = sort;
      }

      // Add filters
      Object.keys(filters).forEach(key => {
        params[`filter[${key}]`] = filters[key];
      });

      const response = await api.get(
        `${api_url}/api/v1/retailer/user/${userId}/branch/${branchId}/patient/${patientId}/prescription`,
        {
          params,
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        }
      );

      console.log('Patient prescriptions response:', response.data);

      // Return the data array from the response
      return response.data.data || [];
    } catch (error) {
      console.error('Error in fetchPatientPrescriptions:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      return rejectWithValue({
        message: error.response?.data?.message || error.message,
        status: error.response?.status
      });
    }
  }
);

const prescriptionsSlice = createSlice({
  name: 'prescriptions',
  initialState,
  reducers: {
    // Optional but if you will implement becareful: Add actions to manually set sort and filters
    setSorting: (state, action) => {
      state.sort = action.payload;
    },
    setFilters: (state, action) => {
      state.filters = action.payload;
    },
    clearFilters: (state) => {
      state.filters = {};
      state.sort = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPrescriptions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPrescriptions.fulfilled, (state, action) => {
        state.loading = false;
        state.prescriptions = action.payload;
        state.sort = action.meta.arg.sort;
        state.filters = action.meta.arg.filters;
      })
      .addCase(fetchPrescriptions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(fetchPrescription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPrescription.fulfilled, (state, action) => {
        state.loading = false;
        state.prescriptionDetail = action.payload.data;
        // Also update the prescription in the list if it exists
        const index = state.prescriptions.findIndex(
          p => p.id === action.payload.data.id
        );
        if (index !== -1) {
          state.prescriptions[index] = action.payload.data;
        }
      })
      .addCase(fetchPrescription.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchPrescriptionComments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPrescriptionComments.fulfilled, (state, action) => {
        state.loading = false;
        state.prescriptionComments = action.payload.data;
        

      })
      .addCase(fetchPrescriptionComments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(addPrescription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addPrescription.fulfilled, (state, action) => {
        state.loading = false;
        state.prescriptions.unshift(action.payload);
        state.currentPrescription = null;
      })
      .addCase(addPrescription.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(addComment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addComment.fulfilled, (state, action) => {
        state.loading = false;
        state.prescriptionComments.unshift(action.payload.data);
      })
      .addCase(addComment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updatePrescription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePrescription.fulfilled, (state, action) => {
        state.loading = false;
        // Update the prescription in the list
        const index = state.prescriptions.findIndex(
          p => p.id === action.payload.id
        );
        if (index !== -1) {
          state.prescriptions[index] = action.payload;
        }
        state.currentPrescription = null;
      })
      .addCase(updatePrescription.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updatePrescriptionStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePrescriptionStatus.fulfilled, (state, action) => {
        state.loading = false;
        // Update the prescription in the list
        const index = state.prescriptions.findIndex(
          p => p.id === action.payload.id
        );
        if (index !== -1) {
          state.prescriptions[index] = action.payload;
        }
        state.currentPrescription = null;
      })
      .addCase(updatePrescriptionStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchMedicationNames.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMedicationNames.fulfilled, (state, action) => {
        state.loading = false;
        state.medicationNames = action.payload;
      })
      .addCase(fetchMedicationNames.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createMedicationName.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createMedicationName.fulfilled, (state, action) => {
        state.loading = false;
        // Add the new medication name to the list
        if (state.medicationNames.data) {
          state.medicationNames.data.push(action.payload);
        }
      })
      .addCase(createMedicationName.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateAllPrescriptions.pending, (state) => {
        // Don't set loading state for save operations
        state.error = null;
      })
      .addCase(updateAllPrescriptions.fulfilled, (state, action) => {
        // Don't set loading state for save operations
        // Update the prescription in the list if it exists
        const updatedPrescription = action.payload.data;
        const index = state.prescriptions.findIndex(
          p => p.id === updatedPrescription.id
        );
        if (index !== -1) {
          state.prescriptions[index] = updatedPrescription;
        }
        // Also update the prescription detail if it's the same prescription
        if (state.prescriptionDetail?.id === updatedPrescription.id) {
          state.prescriptionDetail = updatedPrescription;
        }
      })
      .addCase(updateAllPrescriptions.rejected, (state, action) => {
        // Don't set loading state for save operations
        state.error = action.payload;
      })
      .addCase(fetchPatientPrescriptions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPatientPrescriptions.fulfilled, (state, action) => {
        state.loading = false;
        state.prescriptions = action.payload;
        state.error = null;
      })
      .addCase(fetchPatientPrescriptions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || action.error.message;
        console.error('Error fetching patient prescriptions:', {
          error: action.error,
          payload: action.payload
        });
      });
  },
});

// Export actions for sorting and filtering
export const { setSorting, setFilters, clearFilters } = prescriptionsSlice.actions;

export default prescriptionsSlice.reducer;